import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';
import { MultiSelect } from 'primereact/multiselect';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import useAxios from '../../shared/hooks/useAxios';
import './SavedFiltersDialog.scss';
const FILTERS_ENDPOINT = 'workflow-manager/dashboard/filters';
const FILTER_OPTIONS_ENDPOINT = 'workflow-manager/dashboard/filter-options';
const FILTERS_PAGE_SIZE = 5;
const UI_TO_API_FIELD = {
    country: 'country_id',
    creative_team: 'creative_team',
    category: 'request_category_id',
    status: 'status_id',
    created_by: 'created_by',
    created_date: 'created_date',
    request_type: 'request_type',
};
const API_TO_UI_FIELD = {
    ...Object.fromEntries(Object.entries(UI_TO_API_FIELD).map(([uiKey, apiKey]) => [apiKey, uiKey])),
    country: 'country',
    category: 'category',
    status: 'status',
};
const filterFields = [
    { label: 'Country', key: 'country' },
    // { label: 'Creative Team', key: 'creative_team' },
    { label: 'Status', key: 'status' },
    { label: 'Created By', key: 'created_by' },
    { label: 'Request Type', key: 'request_type' },
];
function payloadOf(response) {
    return response?.data?.data ?? response?.data?.messages?.[0] ?? response?.data ?? [];
}
function asOption(item) {
    if (typeof item === 'string' || typeof item === 'number') {
        return { label: String(item), value: String(item) };
    }
    if (!item || typeof item !== 'object')
        return null;
    const label = item.label
        ?? item.name
        ?? item.display_name
        ?? item.country_name
        ?? item.team_name
        ?? item.category_name
        ?? item.status_name
        ?? item.request_type_name
        ?? item.full_name
        ?? item.title;
    const value = item.value ?? item.id ?? item.code ?? item.key ?? item.uuid ?? label;
    return label == null || value == null ? null : { label: String(label), value: String(value) };
}
function normaliseFilterOptions(payload) {
    const result = {};
    filterFields.forEach(field => {
        const source = payload?.[field.key];
        result[field.key] = Array.isArray(source)
            ? source.map(asOption).filter((option) => option !== null)
            : [];
    });
    return result;
}
function criterionValues(criterion) {
    const raw = criterion.values
        ?? criterion.field_values
        ?? criterion.selected_values
        ?? criterion.field_value
        ?? criterion.selected_value
        ?? criterion.value;
    const values = Array.isArray(raw) ? raw : [raw];
    return values.map(item => {
        if (typeof item === 'string' || typeof item === 'number')
            return item;
        if (item && typeof item === 'object') {
            const option = item;
            return option.value ?? option.id ?? option.code ?? option.key;
        }
        return undefined;
    })
        .filter((value) => typeof value === 'string' || typeof value === 'number')
        .map(value => String(value));
}
function uiFieldKey(fieldKey) {
    return API_TO_UI_FIELD[fieldKey] ?? fieldKey;
}
export default function SavedFiltersDialog({ visible, onHide, selectedFilterId, onSelectFilter, onFilterUpdated, onFilterDeleted, }) {
    const { get, post, put, deleteRequest } = useAxios();
    const [filters, setFilters] = useState([]);
    const [totalFilters, setTotalFilters] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [listError, setListError] = useState('');
    const [formError, setFormError] = useState('');
    const [formVisible, setFormVisible] = useState(false);
    const [editing, setEditing] = useState(null);
    const [name, setName] = useState('');
    const [criteria, setCriteria] = useState([]);
    const [criteriaValues, setCriteriaValues] = useState({});
    const [filterOptions, setFilterOptions] = useState({});
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [optionsError, setOptionsError] = useState('');
    const [isDefault, setIsDefault] = useState(false);
    const loadFilters = async (query = search, page = currentPage) => {
        setLoading(true);
        setListError('');
        try {
            const params = new URLSearchParams({ page: String(page), page_size: String(FILTERS_PAGE_SIZE) });
            if (query.trim())
                params.set('query', query.trim());
            const response = await get(`${FILTERS_ENDPOINT}?${params.toString()}`, { skipSpinner: true });
            const payload = payloadOf(response);
            const rows = Array.isArray(payload) ? payload : (payload.filters ?? payload.saved_filters ?? payload.items ?? []);
            setFilters(rows);
            setTotalFilters(response?.data?.total
                ?? payload.total
                ?? payload.total_count
                ?? rows.length);
            setCurrentPage(response?.data?.page ?? payload.page ?? page);
        }
        catch {
            setListError('Unable to load saved filters.');
            setFilters([]);
            setTotalFilters(0);
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (!visible || formVisible)
            return undefined;
        const timer = window.setTimeout(() => loadFilters(search, currentPage), 350);
        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, formVisible, search, currentPage]);
    const totalPages = Math.max(1, Math.ceil(totalFilters / FILTERS_PAGE_SIZE));
    const firstRecord = totalFilters === 0 ? 0 : ((currentPage - 1) * FILTERS_PAGE_SIZE) + 1;
    const lastRecord = totalFilters === 0 ? 0 : Math.min(firstRecord + filters.length - 1, totalFilters);
    const loadFilterOptions = async () => {
        setOptionsLoading(true);
        setOptionsError('');
        try {
            const response = await get(FILTER_OPTIONS_ENDPOINT, { skipSpinner: true });
            setFilterOptions(normaliseFilterOptions(payloadOf(response)));
        }
        catch {
            setFilterOptions({});
            setOptionsError('Unable to load filter options.');
        }
        finally {
            setOptionsLoading(false);
        }
    };
    const openForm = (filter) => {
        const groupedValues = {};
        (filter?.criteria ?? []).forEach(item => {
            const key = uiFieldKey(item.field_key);
            groupedValues[key] = [...(groupedValues[key] ?? []), ...criterionValues(item)];
        });
        setEditing(filter ?? null);
        setName(filter?.name ?? '');
        setCriteria([...new Set((filter?.criteria ?? []).map(item => uiFieldKey(item.field_key)))]);
        setCriteriaValues(groupedValues);
        setIsDefault(filter?.is_default ?? false);
        setFormError('');
        setFormVisible(true);
        loadFilterOptions();
    };
    const closeForm = () => {
        setFormVisible(false);
        setFormError('');
        setEditing(null);
    };
    const updateCriterion = (key, values) => {
        setCriteriaValues(current => ({ ...current, [key]: values }));
        setCriteria(current => values.length
            ? (current.includes(key) ? current : [...current, key])
            : current.filter(item => item !== key));
    };
    const clearCriteria = () => {
        setCriteria([]);
        setCriteriaValues({});
    };
    const saveFilter = async () => {
        if (!name.trim()) {
            setFormError('Filter name is required.');
            return;
        }
        setLoading(true);
        setFormError('');
        const savedCriteria = criteria.flatMap(fieldKey => {
            const apiFieldKey = UI_TO_API_FIELD[fieldKey] ?? fieldKey;
            const values = criteriaValues[fieldKey] ?? [];
            return values.map(fieldValue => ({
                field_key: apiFieldKey,
                field_value: String(fieldValue),
            }));
        });
        const body = {
            name: name.trim(),
            ...(editing ? {} : { visibility: 'private' }),
            is_default: isDefault,
            criteria: savedCriteria,
        };
        try {
            if (editing) {
                const response = await put(`${FILTERS_ENDPOINT}/${editing.id}`, body);
                const responsePayload = payloadOf(response);
                const updatedFilter = {
                    ...editing,
                    ...body,
                    ...(responsePayload && !Array.isArray(responsePayload) ? responsePayload : {}),
                    id: editing.id,
                    criteria: body.criteria,
                };
                onFilterUpdated?.(updatedFilter);
            }
            else {
                await post(FILTERS_ENDPOINT, body);
            }
            closeForm();
            await loadFilters(search, currentPage);
        }
        catch {
            setFormError(`Unable to ${editing ? 'update' : 'create'} the saved filter.`);
        }
        finally {
            setLoading(false);
        }
    };
    const removeFilter = async (filter) => {
        setLoading(true);
        setListError('');
        try {
            await deleteRequest(`${FILTERS_ENDPOINT}/${filter.id}`);
            onFilterDeleted?.(filter.id);
            const targetPage = filters.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
            if (targetPage !== currentPage)
                setCurrentPage(targetPage);
            else
                await loadFilters(search, targetPage);
        }
        catch {
            setListError('Unable to delete the saved filter.');
        }
        finally {
            setLoading(false);
        }
    };
    const confirmRemoveFilter = (filter) => {
        const isActive = selectedFilterId === filter.id;
        confirmDialog({
            header: 'Delete filter',
            message: (_jsxs("span", { className: "sf-delete-message", children: [_jsxs("strong", { children: ["Delete \u201C", filter.name, "\u201D?"] }), _jsx("span", { children: isActive
                            ? 'This filter is currently applied. After deletion, the request grid will return to Show All.'
                            : 'This saved filter will be permanently deleted. This action cannot be undone.' })] })),
            icon: 'pi pi-trash',
            acceptLabel: 'Delete',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-danger',
            accept: () => removeFilter(filter),
        });
    };
    return (_jsxs(_Fragment, { children: [_jsx(ConfirmDialog, { className: "sf-delete-confirm" }), _jsxs(Dialog, { visible: visible && !formVisible, onHide: onHide, header: _jsxs("span", { className: "sf-dialog-title", children: [_jsx("i", { className: "pi pi-bookmark" }), "Saved Filters"] }), className: "saved-filters-dialog", modal: true, draggable: false, children: [_jsxs("div", { className: "sf-toolbar", children: [_jsx("span", { className: "sf-search", children: _jsx(InputText, { value: search, onChange: event => { setSearch(event.target.value); setCurrentPage(1); }, placeholder: "Search Saved Filters..." }) }), _jsx(Button, { label: "Create New Filter", icon: "pi pi-plus", onClick: () => openForm() })] }), listError && _jsx("div", { className: "sf-error", children: listError }), _jsxs("div", { className: "sf-list", "aria-busy": loading, children: [!loading && !filters.length && _jsx("div", { className: "sf-empty", children: "No saved filters found." }), filters.map(filter => (_jsxs("div", { className: `sf-item ${selectedFilterId === filter.id ? 'selected' : ''}`, role: onSelectFilter ? 'button' : undefined, tabIndex: onSelectFilter ? 0 : undefined, onClick: () => onSelectFilter?.(filter), onKeyDown: (event) => {
                                    if (onSelectFilter && (event.key === 'Enter' || event.key === ' ')) {
                                        event.preventDefault();
                                        onSelectFilter(filter);
                                    }
                                }, children: [_jsx("span", { className: "sf-filter-icon", children: _jsx("i", { className: "pi pi-filter" }) }), _jsxs("div", { className: "sf-item-content", children: [_jsx("strong", { children: filter.name }), _jsxs("div", { className: "sf-tags", children: [[...new Set((filter.criteria ?? []).map(item => uiFieldKey(item.field_key)))].map(fieldKey => (_jsx("span", { children: filterFields.find(option => option.key === fieldKey)?.label ?? fieldKey }, fieldKey))), filter.is_default && _jsx("span", { children: "Default" })] })] }), _jsxs("div", { className: "sf-item-actions", children: [_jsx(Button, { icon: "pi pi-pencil", text: true, rounded: true, "aria-label": `Edit ${filter.name}`, onClick: (event) => { event.stopPropagation(); openForm(filter); } }), _jsx(Button, { icon: "pi pi-trash", text: true, rounded: true, severity: "danger", "aria-label": `Delete ${filter.name}`, onClick: (event) => { event.stopPropagation(); confirmRemoveFilter(filter); } })] })] }, filter.id)))] }), _jsxs("div", { className: "sf-footer", children: [_jsxs("span", { children: ["Showing ", firstRecord, " to ", lastRecord, " of ", totalFilters, " items"] }), _jsxs("div", { className: "sf-pagination", children: [_jsx("button", { type: "button", className: "sf-page-nav", "aria-label": "Previous page", disabled: currentPage <= 1 || loading, onClick: () => setCurrentPage(page => page - 1), children: _jsx("span", { "aria-hidden": "true", children: "\u2039" }) }), _jsx("span", { className: "sf-current-page", children: currentPage }), _jsx("button", { type: "button", className: "sf-page-nav", "aria-label": "Next page", disabled: currentPage >= totalPages || loading, onClick: () => setCurrentPage(page => page + 1), children: _jsx("span", { "aria-hidden": "true", children: "\u203A" }) })] })] })] }), _jsxs(Dialog, { visible: formVisible, onHide: closeForm, header: editing ? 'Edit Filter' : 'Create New Filter', className: "saved-filter-form-dialog", modal: true, draggable: false, footer: _jsxs("div", { className: "sf-form-footer", children: [_jsx(Button, { label: "Cancel", className: "sf-cancel-button", onClick: closeForm }), _jsx(Button, { label: "Save Filter", className: "sf-save-button", loading: loading, onClick: saveFilter })] }), children: [_jsxs("label", { className: "sf-label", htmlFor: "saved-filter-name", children: ["Filter Name ", _jsx("span", { children: "*" })] }), _jsx(InputText, { id: "saved-filter-name", value: name, onChange: event => setName(event.target.value), placeholder: "Enter filter name" }), _jsxs("div", { className: "sf-criteria-header", children: [_jsx("strong", { children: "Save Current Criteria" }), _jsx("button", { type: "button", onClick: clearCriteria, children: "Clear All" })] }), _jsx("div", { className: "sf-criteria-grid", children: filterFields.map(field => (_jsx(MultiSelect, { value: criteriaValues[field.key] ?? [], options: filterOptions[field.key] ?? [], onChange: event => updateCriterion(field.key, event.value ?? []), optionLabel: "label", optionValue: "value", placeholder: field.label, selectedItemsLabel: field.label, maxSelectedLabels: 0, filter: true, filterPlaceholder: "Search Here", emptyMessage: optionsLoading ? 'Loading...' : 'No options found', className: `sf-filter-control ${criteria.includes(field.key) ? 'selected' : ''}`, panelClassName: "sf-filter-options-panel", disabled: optionsLoading }, field.key))) }), _jsxs("div", { className: "sf-default", children: [_jsxs("div", { children: [_jsxs("strong", { children: ["Set as Default ", _jsx("span", { children: "(Optional)" })] }), _jsx("small", { children: "If enabled, this filter will be applied by default every time you open the dashboard." })] }), _jsx(InputSwitch, { checked: isDefault, onChange: event => setIsDefault(event.value) })] }), optionsError && _jsx("div", { className: "sf-error", children: optionsError }), formError && _jsx("div", { className: "sf-error", children: formError })] })] }));
}
